import { useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  FilterFn,
  PaginationState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button, Form, Table } from "react-bootstrap";

import { isRowActive } from "./FeatureFlagApi";
import FeatureFlagListPagination from "./FeatureFlagListPagination";
import FeatureFlagListToolbar from "./FeatureFlagListToolbar";
import FormatFeatureFlagDate from "./FormatFeatureFlagDate";

/** One tenant or facility assignment shown in the details panel tables. */
export interface FeatureFlagAssignmentRow {
  /** ConditionalFeatureFlagTenant or ConditionalFeatureFlagFacility row id. */
  id: number;
  /** Tenant or facility display name. */
  name: string;
  /** When the flag started (or starts) applying. */
  startDate: Date | null;
  /** When the flag stops applying, if an end is set. */
  endDate: Date | null;
}

interface FeatureFlagAssignmentTableProps {
  /** "Tenant" or "Facility"; used for the name column, search label, and add button. */
  itemLabel: string;
  rows: FeatureFlagAssignmentRow[];
  /** Unique id prefix so inputs stay unique when several tables are on the page. */
  idPrefix: string;
  /**
   * Called when a row's Delete button is clicked. While absent the button is
   * display-only (that delete is not wired yet).
   */
  onDeleteRow?: (row: FeatureFlagAssignmentRow) => void;
}

const nameFilter: FilterFn<FeatureFlagAssignmentRow> = (row, _columnId, filterValue) => {
  const query = String(filterValue ?? "").trim().toLowerCase();
  return !query || String(row.original.name ?? "").toLowerCase().includes(query);
};

/** Sorts missing dates before every real date. */
function compareNullableDates(a: Date | null, b: Date | null): number {
  const aTimestamp = a ? a.getTime() : Number.NEGATIVE_INFINITY;
  const bTimestamp = b ? b.getTime() : Number.NEGATIVE_INFINITY;
  return aTimestamp === bTimestamp ? 0 : aTimestamp > bTimestamp ? 1 : -1;
}

/**
 * Tenant/facility assignment table for the feature flag details panel, with
 * search, per-column sorting, and pagination. Enabled switches are shown
 * disabled, Add stays display-only, and Delete fires
 * <see>onDeleteRow</see> when provided (display-only otherwise).
 */
function FeatureFlagAssignmentTable({
  itemLabel,
  rows,
  idPrefix,
  onDeleteRow,
}: FeatureFlagAssignmentTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  const columns = useMemo<ColumnDef<FeatureFlagAssignmentRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: itemLabel,
        enableSorting: true,
        enableGlobalFilter: true,
        cell: ({ getValue }) => (
          <span className="truncatedName" title={String(getValue() ?? "")}>
            {String(getValue() ?? "")}
          </span>
        ),
      },
      {
        accessorKey: "startDate",
        header: "Start Date",
        enableSorting: true,
        enableGlobalFilter: false,
        sortingFn: (rowA, rowB, columnId) =>
          compareNullableDates(
            rowA.getValue<Date | null>(columnId),
            rowB.getValue<Date | null>(columnId)
          ),
        cell: ({ getValue }) => FormatFeatureFlagDate(getValue() as Date | null),
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        enableSorting: true,
        enableGlobalFilter: false,
        sortingFn: (rowA, rowB, columnId) =>
          compareNullableDates(
            rowA.getValue<Date | null>(columnId),
            rowB.getValue<Date | null>(columnId)
          ),
        cell: ({ getValue }) => FormatFeatureFlagDate(getValue() as Date | null),
      },
      {
        id: "actions",
        // Keep header empty — label would overflow the compact actions column.
        header: "",
        enableSorting: false,
        enableGlobalFilter: false,
        cell: ({ row }) => (
          <div className="rowActions">
            <div className="rowActionButtons">
              <Form.Check
                type="switch"
                id={`${idPrefix}-enabled-${row.original.id}`}
                className="mb-0 enabledSwitch"
                checked={isRowActive(row.original.startDate, row.original.endDate)}
                // Row-level enable/disable is not part of story 20247 (only row
                // deletes are); the switch shows the row's current state but
                // stays inert until that write is defined and wired.
                disabled
                aria-label={`Enabled status for ${itemLabel.toLowerCase()} ${row.original.name}`}
              />
              <Button
                variant="link"
                size="sm"
                type="button"
                className="text-danger deleteButton"
                aria-label={`Delete ${itemLabel.toLowerCase()} ${row.original.name}`}
                onClick={onDeleteRow ? () => onDeleteRow(row.original) : undefined}
              >
                <i className="fa fa-trash" />
              </Button>
            </div>
          </div>
        ),
      },
    ],
    [idPrefix, itemLabel, onDeleteRow]
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: nameFilter,
    // Keep the current page when assignment data refreshes; search resets the
    // page itself and the effect below clamps overflow.
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const filteredRowCount = table.getFilteredRowModel().rows.length;

  // When rows disappear, the current page can fall past the end; snap back
  // to the new last page.
  const pageCount = table.getPageCount();
  useEffect(() => {
    if (pageCount > 0 && pagination.pageIndex > pageCount - 1) {
      setPagination((current) => ({ ...current, pageIndex: pageCount - 1 }));
    }
  }, [pageCount, pagination.pageIndex]);

  return (
    <div className="featureFlagList assignmentTable">
      <FeatureFlagListToolbar
        pageSize={pagination.pageSize}
        searchValue={globalFilter}
        searchInputId={`${idPrefix}-search`}
        searchAriaLabel={`Search by ${itemLabel.toLowerCase()} name`}
        onPageSizeChange={(pageSize) =>
          setPagination({ pageIndex: 0, pageSize })
        }
        onSearchChange={(value) => {
          setGlobalFilter(value);
          setPagination((current) => ({ ...current, pageIndex: 0 }));
        }}
      />

      <Table className="featureFlagTable table-striped mb-0">
        <colgroup>
          <col className="assignmentNameCol" />
          <col className="assignmentDateCol" />
          <col className="assignmentDateCol" />
          <col className="assignmentEnabledCol" />
        </colgroup>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();

                return (
                  <th
                    key={header.id}
                    className={canSort ? "sortableHeader" : undefined}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    aria-sort={
                      sorted === "asc"
                        ? "ascending"
                        : sorted === "desc"
                          ? "descending"
                          : canSort
                            ? "none"
                            : undefined
                    }
                  >
                    <span className="headerContent">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && (
                        <span className="sortIcons" aria-hidden="true">
                          <i className={`fa fa-caret-up${sorted === "asc" ? " is-active" : ""}`} />
                          <i className={`fa fa-caret-down${sorted === "desc" ? " is-active" : ""}`} />
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center text-muted py-3">
                No {itemLabel.toLowerCase()} assignments found.
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <FeatureFlagListPagination
        pageIndex={pagination.pageIndex}
        pageCount={table.getPageCount()}
        pageSize={pagination.pageSize}
        filteredRowCount={filteredRowCount}
        canPreviousPage={table.getCanPreviousPage()}
        canNextPage={table.getCanNextPage()}
        onPreviousPage={() => table.previousPage()}
        onNextPage={() => table.nextPage()}
        onGoToPage={(page) => table.setPageIndex(page)}
      />

      <div className="assignmentTableActions">
        <Button variant="primary" size="sm" type="button">
          Add {itemLabel}
        </Button>
      </div>
    </div>
  );
}

export default FeatureFlagAssignmentTable;
